import {
  CRITICALITY_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  displayPortId,
  formatLifecycleForDocument,
  type ConnectionType,
  type DiagramDocument,
  type DiagramEdge,
  type DiagramNode,
} from '@ukrop-nm/shared';
import type { DrawingSheetMeta } from './drawingMeta';
import { buildPurposeText, formatDrawingDateUk } from './drawingMeta';
import { TEXT_PAGE_LAYOUT_MM as M } from './textPageLayout';

const DASH = '—';
const CONNECTION_TYPE_UK: Record<ConnectionType, string> = {
  ethernet: 'Ethernet',
  fiber: 'Оптоволокно',
  wifi: 'Wi‑Fi',
  wan: 'WAN',
  sip: 'SIP / телефонія',
  logical: 'Логічне',
  unknown: 'Невідомо',
};

function cell(v: string | number | null | undefined): string {
  const t = v == null ? '' : String(v).trim();
  return t || DASH;
}

function nodeById(doc: DiagramDocument, id: string): DiagramNode | undefined {
  return doc.nodes.find((n) => n.id === id);
}

function uniqueCidrs(edges: DiagramEdge[]): string[] {
  const set = new Set<string>();
  for (const e of edges) {
    const c = e.networkCidr?.trim();
    if (c) set.add(c);
  }
  return [...set].sort();
}

function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function table(headers: string[], rows: string[][]): string {
  const head = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const body =
    rows.length === 0
      ? `<tr><td colspan="${headers.length}">Немає записів у схемі.</td></tr>`
      : rows
          .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`)
          .join('');
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/** Off-screen A4 HTML for TIA-606-C / ISO/IEC 14763-2 / ISO/IEC/IEEE 42010-aligned project pack. */
export function buildNetworkProjectHtml(
  doc: DiagramDocument,
  topologyJpegDataUrl?: string | null,
  meta?: DrawingSheetMeta | null,
  opts?: { includeTopology?: boolean },
): HTMLElement {
  const includeTopology = opts?.includeTopology !== false;
  const issued = meta?.date
    ? formatDrawingDateUk(meta.date)
    : new Date().toLocaleString('uk-UA');
  const lifecycle = formatLifecycleForDocument(doc.lifecycle);
  const zones = doc.nodes.filter((n) => n.type === 'zone');
  const devices = doc.nodes.filter((n) => n.type !== 'zone');
  const cidrs = uniqueCidrs(doc.edges);
  const docId = meta?.sheetCode?.trim() || `UKROP-NM-NET-${doc.siteId}-V1`;
  const objectName = meta?.objectName?.trim() || doc.name || doc.siteId;
  const author = meta?.author?.trim() || 'Косенко Олексій (UKROP)';
  const projectName = meta?.projectName?.trim() || objectName;
  const purposeParagraphs = (
    meta
      ? buildPurposeText(doc, meta)
      : 'Зафіксувати склад обладнання, каналів і підмереж на схемі для впровадження та моніторингу.'
  )
    .split('\n')
    .map((p) => p.trim())
    .filter(Boolean);

  const zoneRows = zones.map((z, i) => [
    `SP-${String(i + 1).padStart(2, '0')}`,
    cell(z.label),
    cell(z.notes || z.description),
  ]);

  const deviceRows = devices.map((n, i) => {
    const inv = n.inventory ?? {};
    return [
      `EQ-${String(i + 1).padStart(3, '0')}`,
      cell(n.label),
      cell(DEVICE_TYPE_LABELS_UK[n.type]),
      cell(inv.vendor),
      cell(inv.model),
      cell(inv.ipLocal),
      cell(inv.macLocal),
      cell(n.monitorKey),
      cell(CRITICALITY_LABELS_UK[n.criticality]),
      cell(inv.location),
    ];
  });

  const linkRows = doc.edges.map((e, i) => {
    const src = nodeById(doc, e.sourceNodeId);
    const tgt = nodeById(doc, e.targetNodeId);
    return [
      `LK-${String(i + 1).padStart(3, '0')}`,
      cell(e.label || e.monitorKey),
      cell(src?.label),
      cell(displayPortId(e.sourcePort)),
      cell(tgt?.label),
      cell(displayPortId(e.targetPort)),
      cell(CONNECTION_TYPE_UK[e.connectionType] ?? e.connectionType),
      cell(e.networkCidr),
      cell(CRITICALITY_LABELS_UK[e.criticality]),
    ];
  });

  const subnetRows = cidrs.map((c, i) => [
    `SN-${String(i + 1).padStart(2, '0')}`,
    c,
    String(doc.edges.filter((e) => e.networkCidr?.trim() === c).length),
  ]);

  const ipRows = devices
    .filter((n) => n.inventory?.ipLocal?.trim())
    .map((n) => [
      cell(n.label),
      cell(n.inventory?.ipLocal),
      cell(n.monitorKey),
    ]);

  const root = document.createElement('div');
  root.setAttribute('data-ukrop-project-doc', '1');
  root.innerHTML = `
    <style>
      .ukrop-proj { width: ${M.width}mm; box-sizing: border-box;
        padding: 0 ${M.right}mm 0 ${M.left}mm; background: #fff; color: #1d2939;
        font: 12px/1.45 "Segoe UI", "Noto Sans", Arial, sans-serif; }
      .ukrop-proj h1 { font-size: 20px; margin: 0 0 6px; }
      .ukrop-proj h2 { font-size: 14px; margin: 22px 0 8px; border-bottom: 1px solid #d0d5dd; padding-bottom: 4px; }
      .ukrop-proj p, .ukrop-proj li { font-size: 11.5px; color: #344054; }
      .ukrop-proj p.doc-para { text-indent: ${M.firstLineIndent}mm; margin: 0 0 0.55em; text-align: justify; }
      .ukrop-proj .meta { font-size: 11px; color: #475467; }
      .ukrop-proj .std { font-size: 10.5px; color: #667085; margin-top: 8px; }
      .ukrop-proj table { width: 100%; border-collapse: collapse; margin: 8px 0 4px; table-layout: fixed; }
      .ukrop-proj th, .ukrop-proj td { border: 1px solid #d0d5dd; padding: 4px 5px; text-align: left;
        font-size: 10px; word-wrap: break-word; vertical-align: top; }
      .ukrop-proj th { background: #f2f4f7; font-weight: 700; }
      .ukrop-proj img.topo { width: 100%; border: 1px solid #d0d5dd; margin: 8px 0; }
      .ukrop-proj .note { font-size: 10.5px; color: #b54708; }
    </style>
    <div class="ukrop-proj">
      <p class="meta">${escapeHtml(docId)} · ${escapeHtml(issued)} · рев. ${escapeHtml(meta?.revision || 'A')}</p>
      <h1>${escapeHtml(meta?.drawingTitle || 'Технічний опис мережі')}</h1>
      <p><strong>Організація:</strong> ${escapeHtml(meta?.organization || 'UKROP')} &nbsp;|&nbsp;
         <strong>Проєкт:</strong> ${escapeHtml(projectName)}</p>
      <p><strong>Обʼєкт:</strong> ${escapeHtml(objectName)} &nbsp;|&nbsp;
         <strong>Ідентифікатор:</strong> ${escapeHtml(doc.siteId)} &nbsp;|&nbsp;
         <strong>Стадія проєкту:</strong> ${escapeHtml(lifecycle.label)}</p>
      <p class="meta">${escapeHtml(lifecycle.description)}</p>
      <p><strong>Розробив:</strong> ${escapeHtml(author)}
         ${meta?.checkedBy?.trim() ? ` &nbsp;|&nbsp; <strong>Перевірив:</strong> ${escapeHtml(meta.checkedBy)}` : ''}</p>
      <p class="std doc-para">Документ підготовлено <strong>за принципами</strong> TIA-606-C (адміністрування
        телекомунікаційної інфраструктури: ідентифікатори просторів, обладнання та ліній),
        ISO/IEC 14763-2 (документування впровадження), ISO/IEC/IEEE 42010 (опис архітектури)
        та ISO 7200 (основний напис креслення). Це не сертифікація відповідності зазначеним стандартам.</p>

      <h2>1. Призначення</h2>
      ${purposeParagraphs.map((p) => `<p class="doc-para">${escapeHtml(p)}</p>`).join('\n      ')}

      <h2>2. Вид архітектури — логічна топологія</h2>
      ${
        includeTopology && topologyJpegDataUrl
          ? `<img class="topo" alt="Топологія мережі" src="${topologyJpegDataUrl}" />`
          : includeTopology
            ? `<p class="note">TODO: малюнок топології не згенеровано (порожня схема або збій знімка полотна).</p>`
            : `<p class="doc-para">Креслення логічної топології наведено на аркуші 1 цього PDF (висока якість, формат A4 landscape).</p>`
      }

      <h2>3. Простори / зони (запис TIA-606 space)</h2>
      ${table(['ID', 'Назва зони', 'Примітка'], zoneRows)}

      <h2>4. Реєстр обладнання (хто / що)</h2>
      ${table(
        ['ID', 'Назва', 'Тип', 'Виробник', 'Модель', 'IP', 'MAC', 'monitorKey', 'Критичність', 'Розташування'],
        deviceRows,
      )}
      <p class="note">IP і MAC зберігаються лише локально в схемі і не входять у cloud telemetry.</p>

      <h2>5. Реєстр зʼєднань (канали / лінії)</h2>
      ${table(
        ['ID', 'Підпис', 'Від', 'Порт', 'До', 'Порт', 'Тип', 'Мережа CIDR', 'Критичність'],
        linkRows,
      )}

      <h2>6. Мережі та підмережі</h2>
      ${table(['ID', 'CIDR (мережа/маска)', 'К-сть ліній'], subnetRows)}
      <h2>7. Призначені IP на обладнанні</h2>
      ${table(['Обладнання', 'IP Address', 'monitorKey'], ipRows)}

      <h2>8. Готовність до моніторингу</h2>
      <p class="doc-para">Ключі моніторингу з схеми (для подальшого скрипта RouterOS / симулятора):
        ${
          devices.filter((n) => n.monitorKey).length
            ? escapeHtml(devices.map((n) => n.monitorKey).filter(Boolean).join(', '))
            : DASH
        }.</p>

      <h2>9. Обмеження цього випуску</h2>
      <ul>
        <li>Порожні поля схеми показані як «${DASH}» — не вигадані.</li>
        <li>Документ відображає поточний стан локальної схеми на момент експорту.</li>
        <li>Фізичні траси кабелю, довжини, категорія СКС, VLAN і вимірювання — TODO, якщо не введені в схему.</li>
      </ul>
    </div>
  `;
  return root;
}
